import { KeyboardEvent, useEffect, useState } from 'react';
import { Button, Select, TimePicker, App, Card } from 'antd';
import { DayOfWeek, PowerHourConfig } from '../../../../dto/reporting-models';
import { reportingService } from '../../../../services/reporting-service';
import { useMsal } from '@azure/msal-react';
import { GetAccessToken } from '../../../../utils/auth-utils';
import { scopes } from '../../../../authConfig';
import { ClockCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { strings } from '../../../../lang';

export const PowerHourConfigContainter = () => {
  const [powerHourConfigs, setPowerHourConfigs] = useState<PowerHourConfig[]>([]);
  const { notification } = App.useApp();
  const { instance, inProgress } = useMsal();
  const labels = strings.gamificationSettings.powerHour;
  const days = strings.gamificationSettings.achievements.daysOfWeek;
  const dayLabels: Record<string, string> = {
    Sunday: days.sunday,
    Monday: days.monday,
    Tuesday: days.tuesday,
    Wednesday: days.wednesday,
    Thursday: days.thursday,
    Friday: days.friday,
    Saturday: days.saturday,
  };

  useEffect(() => {
    const fetchPowerHourConfigs = async () => {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const configs = await reportingService.getPowerHourConfigs(token.accessToken);
      setPowerHourConfigs(configs);
    };

    fetchPowerHourConfigs();
  }, []);

  const addRow = () => {
    setPowerHourConfigs([...powerHourConfigs, { fromDayOfWeek: DayOfWeek.Monday, untilDayOfWeek: DayOfWeek.Monday, startTimeUtc: '', feId: crypto.randomUUID() } as PowerHourConfig]);
  };

  const handleAddCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      addRow();
    }
  };

  const updateRow = (updatedConfig: PowerHourConfig) => {
    const updatedConfigs = powerHourConfigs.map((config) => (config.feId === updatedConfig.feId ? updatedConfig : config));
    //this deletes any configs that have been added but removed before getting saved, aka temporary configs
    const updatedWithNoTemporary = updatedConfig.isDeleted && !updatedConfig.id ? updatedConfigs.filter((config) => config.feId !== updatedConfig.feId) : updatedConfigs;

    setPowerHourConfigs(updatedWithNoTemporary);
  };

  const saveChanges = async () => {
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);

    const hasInvalidConfig = powerHourConfigs.some((config) => {
      return !config.isDeleted && (isNaN(config.fromDayOfWeek) || isNaN(config.untilDayOfWeek) || !config.startTimeUtc || !config.endTimeUtc);
    });

    if (hasInvalidConfig) {
      notification.error({
        message: labels.validationError,
        description: labels.validationDescription,
      });
      return;
    }

    const isUpdated = await reportingService.upsertPowerHourConfigs(token.accessToken, powerHourConfigs);

    isUpdated
      ? notification.success({
          message: labels.saveSuccess,
          description: labels.saveSuccessDescription,
        })
      : notification.error({
          message: labels.saveError,
          description: labels.saveErrorDescription,
        });
  };

  return (
    <div className="container flex-admin-vertical-container" style={{ width: 900 }}>
      <Card className="power-hour-config__add-card" role="button" aria-label={labels.addWindow} tabIndex={0} onClick={addRow} onKeyDown={handleAddCardKeyDown}>
        <div className="power-hour-config__add-content">
          <span className="power-hour-config__add-icon" aria-hidden="true" />
          <div>
            <strong>{labels.addWindow}</strong>
            <span>{labels.addWindowDescription}</span>
          </div>
        </div>
      </Card>
      {powerHourConfigs
        .filter((x) => !x.isDeleted)
        .map((config, index) => (
          <div className="power-hour-config__row" key={index}>
            <div>
              <Select
                style={{ width: '100%' }}
                placeholder={labels.fromDay}
                value={DayOfWeek[config.fromDayOfWeek]}
                onChange={(value) => updateRow({ ...config, fromDayOfWeek: DayOfWeek[value as keyof typeof DayOfWeek] })}
              >
                {Object.values(DayOfWeek)
                  .filter((x) => isNaN(Number(x)) && x != 'Sunday')
                  .map((day) => (
                    <Select.Option key={day} value={day}>
                      {dayLabels[day]}
                    </Select.Option>
                  ))}
              </Select>
            </div>
            <div>
              <Select
                style={{ width: '100%' }}
                placeholder={labels.untilDay}
                value={DayOfWeek[config.untilDayOfWeek]}
                onChange={(value) => updateRow({ ...config, untilDayOfWeek: DayOfWeek[value as keyof typeof DayOfWeek] })}
              >
                {Object.values(DayOfWeek)
                  .filter((x) => isNaN(Number(x)) && x != 'Sunday')
                  .map((day) => (
                    <Select.Option key={day} value={day}>
                      {dayLabels[day]}
                    </Select.Option>
                  ))}
              </Select>
            </div>
            <div>
              <TimePicker
                placeholder={labels.startTime}
                value={config.startTimeUtc ? dayjs(`1970-01-01T${config.startTimeUtc}`) : null}
                onChange={(time) => updateRow({ ...config, startTimeUtc: time ? time.format('HH:mm') : '' })}
                format="HH:mm"
                minuteStep={15}
                style={{ width: '100%' }}
                suffixIcon={<ClockCircleOutlined />}
              />
            </div>
            <div>
              <TimePicker
                placeholder={labels.endTime}
                value={config.endTimeUtc ? dayjs(`1970-01-01T${config.endTimeUtc}`) : null}
                onChange={(time) => updateRow({ ...config, endTimeUtc: time ? time.format('HH:mm') : '' })}
                format="HH:mm"
                minuteStep={15}
                style={{ width: '100%' }}
                suffixIcon={<ClockCircleOutlined />}
              />
            </div>
            <div className="power-hour-config__delete-action">
              <Button danger icon={<DeleteOutlined />} onClick={() => updateRow({ ...config, isDeleted: true })}>
                {labels.delete}
              </Button>
            </div>
          </div>
        ))}
      <div className="power-hour-config__actions">
        <Button type="primary" onClick={saveChanges}>
          {strings.gamificationSettings.saveChanges}
        </Button>
      </div>
    </div>
  );
};
